import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import api from 'state/axios';
import { differenceInMinutes } from "date-fns";
import toast from "react-hot-toast";
import { Box, Stepper, Step, StepLabel, Button, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import PendingActionsIcon from "@mui/icons-material/PendingActions";


const getStatusIcon = (status) => {
    switch (status) {
        case "completed":
            return <CheckCircleIcon color="success" />;
        case "in-progress":
            return <HourglassTopIcon color="warning" />;
        default:
            return <PendingActionsIcon color="disabled" />;
    }
};

const FlightStepper = ({ flight, onProgressUpdate }) => {
    const user = useSelector((state) => state.user);

    const [localFlight, setLocalFlight] = useState(null);
    const [loadingTask, setLoadingTask] = useState(false);


    // Đồng bộ lại khi chọn chuyến khác
    useEffect(() => {
        setLocalFlight(flight || null);
    }, [flight]);

    // Nếu chưa chọn chuyến
    if (!localFlight || !localFlight.tasks) {
        return (
            <Box
                flexGrow={1}
                display="flex"
                alignItems="center"
                justifyContent="center"
                sx={{ border: "1px dashed #999", borderRadius: 2, minHeight: "300px" }}
            >
                <Typography>Select a flight to view its tasks.</Typography>
            </Box>
        );
    }
    
    const upsertTaskStatus = async (index, newStatus) => {
        const updatedTasks = [...localFlight.tasks];
        const task = updatedTasks[index];

        if (newStatus === "in-progress") {
            task.status = "in-progress";
            task.actualStartDate = new Date();
        } else if (newStatus === "completed") {
            task.status = "completed";
            task.actualEndDate = new Date();
            task.actualDuration = differenceInMinutes(task.actualEndDate, task.actualStartDate);
        }

        try {
            setLoadingTask(true);

            let instanceId = localFlight.instanceId || null;
            let response;

            if (!instanceId) {
                const createPayload = {
                    flightDate: localFlight.flightDate,
                    std: localFlight.std,
                    flightDesc: localFlight.flightDesc,
                    dep: localFlight.dep,
                    templateId: localFlight.templateId,
                    tasks: updatedTasks,
                    progress: 0,
                };

                response = await api.post('/tatinstances/new', createPayload);
                instanceId = response.data?.data?._id || response.data?._id;

                if (!instanceId) throw new Error("Instance Id missing from create response.");
            } else {
                const updatePayload = { tasks: updatedTasks };                
                response = await api.put(`/tatinstances/update/${instanceId}`, updatePayload);
                instanceId = response.data?.data?._id || response.data?._id;
            }

            const total = updatedTasks.length;
            const done = updatedTasks.filter(t => t.status === "completed").length;
            const progress = total ? Math.round((done / total) * 100) : 0;

            setLocalFlight({
                ...localFlight,
                instanceId,
                tasks: updatedTasks,
                progress,
            });

            if (typeof onProgressUpdate === "function") {
                onProgressUpdate?.(
                    instanceId, 
                    progress, 
                    {
                        flightDate: localFlight.flightDate,
                        flightDesc: localFlight.flightDesc,
                        dep: localFlight.dep,
                    }
                );
            }

            toast.success(`${task.taskName} marked as ${newStatus} by ${user.firstName?.toUpperCase() || "user"}`);
        } catch (error) {
            toast.error(error?.message || "Failed to update task status!");
        } finally {
            setLoadingTask(false);
        }
    };

    return (
        <Box flexGrow={1} sx={{ p: 2 }}>
            <Typography variant="h5" sx={{ mb: 0 }}>
                Task Progress
            </Typography>

            <Stepper orientation="vertical" sx={{ borderRadius: 1, p: 1 }}>
                {localFlight.tasks.map((t, i) => (
                    <Step key={t.taskCode || i} completed={t.status === "completed"}>
                        <StepLabel icon={getStatusIcon(t.status)}>
                            <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                width="100%"
                                sx={{ mb: 1 }}
                            >
                                <Box>
                                    <Typography sx={{ fontWeight: "bold" }}>{t.taskName}</Typography>
                                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                        Status: {t.status.toUpperCase()}
                                        {t.actualStartDate && (
                                            <span> | Start: {new Date(t.actualStartDate).toLocaleTimeString()}</span>
                                        )}
                                        {t.actualEndDate && (
                                            <span> | End: {new Date(t.actualEndDate).toLocaleTimeString()}</span>
                                        )}
                                    </Typography>
                                </Box>
                             
                                <Box display="flex" gap={1}>
                                    {t.status === "pending" && (
                                        <Button
                                            variant="contained"
                                            size="small"
                                            disabled={
                                                loadingTask ||
                                                (t.preceding?.length > 0 &&
                                                    !t.preceding.every(code => {
                                                        const depTask = localFlight.tasks.find(x => x.taskCode === code);
                                                        return depTask && depTask.status === "completed";
                                                    }
                                                ))
                                            }
                                            onClick={() => {
                                                if (
                                                    t.preceding?.length > 0 &&
                                                    !t.preceding.every(code => {
                                                        const depTask = localFlight.tasks.find(x => x.taskCode === code);
                                                        return depTask && depTask.status === "completed";
                                                    })
                                                ) {
                                                    toast.error("Please complete all preceding tasks first!");
                                                    return;
                                                }
                                                upsertTaskStatus(i, "in-progress");
                                            }}
                                        >
                                            Start
                                        </Button>
                                    )}
                                    {t.status === "in-progress" && (
                                        <Button
                                            variant="contained"
                                            size="small"
                                            color="success"
                                            disabled={loadingTask}
                                            onClick={() => upsertTaskStatus(i, "completed")}
                                        >
                                            Done
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                        </StepLabel>
                    </Step>
                ))}
            </Stepper>
        </Box>
    );
};

export default FlightStepper;
